import { Card } from 'canoe';
import { compose, connectWithHammock, ENDPOINTS, useActions } from 'hammock';
import moment from 'moment';
import React, { useEffect } from 'react';

import { ContentType } from '@candu-ai/content/types';
import { useEventing } from 'commons/eventing';
import { useSessionStorage } from 'commons/hooks/useSessionStorage';
import { transformStringToIdentifier } from 'commons/utils';
import { replacePlaceholders } from 'dashboard/pages/Content/pages/Generate/components';

import { ContentMetadataForm } from './components/ContentMetadataForm';
import { Stepper } from './components/Stepper';
import { WizardView } from './components/WizardView';
import { CreateContentPayload } from './types';
import { createParagraphstxt, createTitlestxt } from './utils';

export interface TemplateMetadata {
  id: number;
  name: string;
  image?: string;
}

interface CreateWithTemplateProps {
  onSuccessCreate?: (resource: { id: string }) => void;
  template: TemplateMetadata;
  templateDocument: any;
  folderId?: string;
  context: {
    applicationId: string | number;
    newModals?: boolean;
    contentType: ContentType;
    skipForm: boolean;
    hasExtension?: boolean;
    withSnapshots?: boolean;
  };
}

export const CreateWithTemplateImpl = ({
  template,
  templateDocument,
  context,
  onSuccessCreate,
  folderId,
}: CreateWithTemplateProps) => {
  const { create: createContentMeta } = useActions(ENDPOINTS.contentMetadata);
  const { get: getDocument, update: updateDocument } = useActions(ENDPOINTS.documentsEditor);
  const eventing = useEventing();

  const [templateContentString, , , removeTemplateContent] = useSessionStorage(
    `candu.firstMate.templateContent-${template.id}`,
    undefined,
  );

  const templateContent = templateContentString ? JSON.parse(templateContentString) : undefined;

  const rootNode = templateDocument.document.nodes[templateDocument.document.rootNodeId];

  useEffect(() => {
    return () => {
      // if we leave the page without submitting, clear template content session storage
      removeTemplateContent();
    };
  }, []);

  const onSubmit = ({ name, folderId: createFolderId, contentType }: CreateContentPayload) => {
    createContentMeta({
      body: {
        name,
        folderId: createFolderId,
        slug: transformStringToIdentifier(name),
        sourceTemplateId: template.id,
        contentType,
      },
      onSuccessNotification: context?.skipForm ? null : undefined,
      onErrorNotification: context?.skipForm ? null : undefined,
    }).then(async (resource: { id: string; defaultDocumentId: string }) => {
      eventing.track('dashboard.content.created');
      eventing.track('template.createdNewContent', {
        templateId: template.id,
      });

      if (templateContent?.data) {
        // GET document from documentsEditor
        const document = await getDocument({ id: resource.defaultDocumentId });

        // Replace placeholders with AI content
        const updatedDocument = replacePlaceholders(templateContent, document);
        removeTemplateContent();

        // POST new document to documentsEditor
        await updateDocument({
          id: updatedDocument.id,
          body: { document: updatedDocument.document, id: updatedDocument.id },
        });
      }

      onSuccessCreate?.(resource);
    });
  };

  if (context?.skipForm) {
    const payload = {
      name: `${template.name} ${moment(new Date().getTime()).format('YYYY-MM-DD[T]HH:mm:ss')}`,
      contentType: context?.contentType,
      sourceTemplateId: template.id,
    };

    onSubmit(payload);
  }

  if (context?.skipForm) {
    return null;
  }

  return (
    <Card size="xl">
      {rootNode.type !== 'Modal' ||
        (!!context?.withSnapshots && (
          <Stepper currentStep={2} withSnapshots={!!context?.withSnapshots} />
        ))}

      <WizardView
        title={createTitlestxt[context?.contentType || 'Inline']}
        subTitle={createParagraphstxt[context?.contentType || 'Inline']}
      >
        <ContentMetadataForm
          onSubmit={onSubmit}
          folderId={folderId || undefined}
          withSnapshots={context?.withSnapshots}
          contentType={context?.contentType || 'Inline'}
          contentTitle={templateContent?.data?.content_name}
        />
      </WizardView>
    </Card>
  );
};

const withTemplateMetadata = connectWithHammock(({ templateId, notFoundComponent }) => ({
  id: templateId,
  endpoint: ENDPOINTS.templateMetadata,
  notFoundComponent,
  resourceName: 'template',
}));

const withTemplateDocument = connectWithHammock(({ templateId, notFoundComponent }) => ({
  id: templateId,
  endpoint: ENDPOINTS.templateDocumentsEditor,
  notFoundComponent,
  resourceName: 'templateDocument',
}));

export const CreateWithTemplate = compose(
  withTemplateMetadata,
  withTemplateDocument,
)(CreateWithTemplateImpl);
